import { useState, useCallback, useEffect } from "react";
import { ethers } from "ethers";
import {
    otcMarketplaceAddress,
    mcgpTokenAddress,
    sonicUsdcTokenAddress,
    SONIC_RPC_URL,
} from "../blockchain/contracts";
import { otcMarketplaceAbi } from "../blockchain/abi/otcMarketplace";
import abi from "../blockchain/abi";

const { erc20Abi } = abi;

export const useOtcMarketplace = (signer) => {
    const [buyPhases, setBuyPhases] = useState([]);
    const [sellPhases, setSellPhases] = useState([]);
    const [activeBuyPhase, setActiveBuyPhase] = useState(null);
    const [activeSellPhase, setActiveSellPhase] = useState(null);
    const [contractBalances, setContractBalances] = useState({ mcgp: "0", usdc: "0" });
    const [userBalances, setUserBalances] = useState({ mcgp: "0", usdc: "0" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Get read-only provider for fetching data
    // Use wallet provider when available to avoid CORS issues with direct RPC calls
    const getReadProvider = useCallback(() => {
        if (signer && signer.provider) {
            return signer.provider;
        }
        // Fallback to direct RPC (may have CORS issues in browser)
        return new ethers.providers.JsonRpcProvider(SONIC_RPC_URL);
    }, [signer]);

    // Get contract instance (read-only or with signer)
    const getContract = useCallback(
        (withSigner = false) => {
            const provider = withSigner && signer ? signer : getReadProvider();
            return new ethers.Contract(otcMarketplaceAddress, otcMarketplaceAbi, provider);
        },
        [signer, getReadProvider]
    );

    // Get token contract
    const getTokenContract = useCallback(
        (tokenAddress, withSigner = false) => {
            const provider = withSigner && signer ? signer : getReadProvider();
            return new ethers.Contract(tokenAddress, erc20Abi, provider);
        },
        [signer, getReadProvider]
    );

    // Fetch all phases
    const fetchPhases = useCallback(async () => {
        try {
            const contract = getContract();
            const [buyPhasesData, sellPhasesData, activeBuyIdx, activeSellIdx] = await Promise.all([
                contract.getBuyPhases(),
                contract.getSellPhases(),
                contract.activeBuyPhaseIndex(),
                contract.activeSellPhaseIndex(),
            ]);

            const formatPhase = (phase, index, activeIndex) => ({
                index,
                price: phase.price.toString(),
                priceFormatted: (Number(phase.price) / 1e6).toFixed(6),
                isActive: index === activeIndex.toNumber(),
                isBuyPhase: phase.isBuyPhase,
                name: phase.name,
            });

            const formattedBuyPhases = buyPhasesData.map((p, i) => formatPhase(p, i, activeBuyIdx));
            const formattedSellPhases = sellPhasesData.map((p, i) => formatPhase(p, i, activeSellIdx));

            setBuyPhases(formattedBuyPhases);
            setSellPhases(formattedSellPhases);
            setActiveBuyPhase(formattedBuyPhases.find((p) => p.isActive));
            setActiveSellPhase(formattedSellPhases.find((p) => p.isActive));
        } catch (err) {
            console.error("Error fetching phases:", err);
            setError("Failed to fetch phases");
        }
    }, [getContract]);

    // Fetch contract balances
    const fetchContractBalances = useCallback(async () => {
        try {
            const contract = getContract();
            const balances = await contract.getBalances();
            setContractBalances({
                mcgp: ethers.utils.formatUnits(balances.mcgpBalance, 18),
                usdc: ethers.utils.formatUnits(balances.usdcBalance, 6),
            });
        } catch (err) {
            console.error("Error fetching contract balances:", err);
        }
    }, [getContract]);

    // Fetch user balances
    const fetchUserBalances = useCallback(
        async (userAddress) => {
            if (!userAddress) return;

            try {
                const mcgpContract = getTokenContract(mcgpTokenAddress);
                const usdcContract = getTokenContract(sonicUsdcTokenAddress);

                const [mcgpBalance, usdcBalance] = await Promise.all([
                    mcgpContract.balanceOf(userAddress),
                    usdcContract.balanceOf(userAddress),
                ]);

                setUserBalances({
                    mcgp: ethers.utils.formatUnits(mcgpBalance, 18),
                    usdc: ethers.utils.formatUnits(usdcBalance, 6),
                });
            } catch (err) {
                console.error("Error fetching user balances:", err);
            }
        },
        [getTokenContract]
    );

    // Calculate buy price
    const calculateBuyPrice = useCallback(
        async (mcgpAmount) => {
            if (!mcgpAmount || mcgpAmount === "0") return "0";

            try {
                const contract = getContract();
                const amountWei = ethers.utils.parseUnits(mcgpAmount, 18);
                const usdcAmount = await contract.calculateBuyPrice(amountWei);
                return ethers.utils.formatUnits(usdcAmount, 6);
            } catch (err) {
                console.error("Error calculating buy price:", err);
                return "0";
            }
        },
        [getContract]
    );

    // Calculate sell price
    const calculateSellPrice = useCallback(
        async (mcgpAmount) => {
            if (!mcgpAmount || mcgpAmount === "0") return "0";

            try {
                const contract = getContract();
                const amountWei = ethers.utils.parseUnits(mcgpAmount, 18);
                const usdcAmount = await contract.calculateSellPrice(amountWei);
                return ethers.utils.formatUnits(usdcAmount, 6);
            } catch (err) {
                console.error("Error calculating sell price:", err);
                return "0";
            }
        },
        [getContract]
    );

    // Check allowance
    const checkAllowance = useCallback(
        async (tokenAddress, ownerAddress, decimals = 18) => {
            try {
                const tokenContract = getTokenContract(tokenAddress);
                const allowance = await tokenContract.allowance(ownerAddress, otcMarketplaceAddress);
                return ethers.utils.formatUnits(allowance, decimals);
            } catch (err) {
                console.error("Error checking allowance:", err);
                return "0";
            }
        },
        [getTokenContract]
    );

    // Approve tokens
    const approve = useCallback(
        async (tokenAddress, amount, decimals = 18) => {
            if (!signer) throw new Error("Wallet not connected");

            const tokenContract = getTokenContract(tokenAddress, true);
            const amountWei = ethers.utils.parseUnits(amount, decimals);

            const tx = await tokenContract.approve(otcMarketplaceAddress, amountWei);
            await tx.wait();
            return tx;
        },
        [signer, getTokenContract]
    );

    // Buy MCGP with USDC
    const buy = useCallback(
        async (mcgpAmount, maxSlippagePercent = 1) => {
            if (!signer) throw new Error("Wallet not connected");

            setLoading(true);
            setError(null);

            try {
                const contract = getContract(true);
                const amountWei = ethers.utils.parseUnits(mcgpAmount, 18);

                // Calculate expected USDC with slippage
                const expectedUsdc = await contract.calculateBuyPrice(amountWei);
                const maxUsdc = expectedUsdc.mul(100 + maxSlippagePercent).div(100);

                // Check and approve USDC if needed
                const userAddress = await signer.getAddress();
                const usdcContract = getTokenContract(sonicUsdcTokenAddress, true);
                const allowance = await usdcContract.allowance(userAddress, otcMarketplaceAddress);

                if (allowance.lt(maxUsdc)) {
                    const approveTx = await usdcContract.approve(otcMarketplaceAddress, maxUsdc);
                    await approveTx.wait();
                }

                // Execute buy
                const tx = await contract.buy(amountWei, maxUsdc);
                const receipt = await tx.wait();

                // Refresh balances
                await fetchUserBalances(userAddress);
                await fetchContractBalances();

                return receipt;
            } catch (err) {
                console.error("Buy error:", err);
                setError(err.reason || err.message || "Buy failed");
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [signer, getContract, getTokenContract, fetchUserBalances, fetchContractBalances]
    );

    // Sell MCGP for USDC
    const sell = useCallback(
        async (mcgpAmount, maxSlippagePercent = 1) => {
            if (!signer) throw new Error("Wallet not connected");

            setLoading(true);
            setError(null);

            try {
                const contract = getContract(true);
                const amountWei = ethers.utils.parseUnits(mcgpAmount, 18);

                // Calculate expected USDC with slippage
                const expectedUsdc = await contract.calculateSellPrice(amountWei);
                const minUsdc = expectedUsdc.mul(100 - maxSlippagePercent).div(100);

                // Check and approve MCGP if needed
                const userAddress = await signer.getAddress();
                const mcgpContract = getTokenContract(mcgpTokenAddress, true);
                const allowance = await mcgpContract.allowance(userAddress, otcMarketplaceAddress);

                if (allowance.lt(amountWei)) {
                    const approveTx = await mcgpContract.approve(otcMarketplaceAddress, amountWei);
                    await approveTx.wait();
                }

                // Execute sell
                const tx = await contract.sell(amountWei, minUsdc);
                const receipt = await tx.wait();

                // Refresh balances
                await fetchUserBalances(userAddress);
                await fetchContractBalances();

                return receipt;
            } catch (err) {
                console.error("Sell error:", err);
                setError(err.reason || err.message || "Sell failed");
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [signer, getContract, getTokenContract, fetchUserBalances, fetchContractBalances]
    );

    // Initial fetch - only when wallet is connected to avoid CORS issues
    useEffect(() => {
        if (signer) {
            fetchPhases();
            fetchContractBalances();
        }
    }, [signer, fetchPhases, fetchContractBalances]);

    return {
        buyPhases,
        sellPhases,
        activeBuyPhase,
        activeSellPhase,
        contractBalances,
        userBalances,
        loading,
        error,
        fetchPhases,
        fetchContractBalances,
        fetchUserBalances,
        calculateBuyPrice,
        calculateSellPrice,
        checkAllowance,
        approve,
        buy,
        sell,
    };
};

export default useOtcMarketplace;
